// src/components/canine-teeth/ImageUpload.js

import React, { useState, useEffect, useCallback } from 'react';
import ImageSelector from './ImageSelector';
import '../tools.css';
import SavedSessionsGallery from './SavedSessionsGallery';
import { auth, db } from '../../firebase';
// eslint-disable-next-line no-unused-vars
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Circles } from 'react-loader-spinner';

function ImageUpload() {
  const [userEmail, setUserEmail] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState('');
  const [posizione, setPosizione] = useState('');
  const [loadedSession, setLoadedSession] = useState(null);
  const [sesso, setSesso] = useState('');
  const [regione, setRegione] = useState('');
  const [realAge, setRealAge] = useState('');
  const [loadedSubImages, setLoadedSubImages] = useState([]);


  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [userInfo, setUserInfo] = useState(null);

  const [sessionsCount, setSessionsCount] = useState(0);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const navigate = useNavigate();

  const handleSaveSessoAndRegione = useCallback(() => {
    // sesso and regione are optional now; proceed even if one/both are empty
    setStep(3);
  }, []); // sesso/regione are not required as hook dependencies here

  // Authentication state management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        setUserEmail(user.email);
        try {
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserInfo(docSnap.data());
          } else {
            console.warn('No user document found!');
            setUserInfo(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserInfo(null);
        }
      } else {
        setUser(null);
        navigate('/');
      }
      setIsAuthLoading(false); // Authentication completed
    });

    return () => unsubscribe();
  }, [navigate]);

  // Handle image selection
  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setStep(2);
    }
  }, []);

  // Handle session selection
  const handleSelectSession = useCallback((session, subImages) => {
    setImageFile(null);
    setTipo(session.tipo || '');
    setPosizione(session.posizione || '');
    setSesso(session.sesso || '');
    setRegione(session.regione || '');
    setRealAge(session.realAge || '');
    setLoadedSession(session);
    setLoadedSubImages(subImages); // Set the loaded sub-images
    setStep(3);
  }, []);


  // Update sessions count
  const updateSessionsCount = useCallback(() => {
    setSessionsCount((prev) => prev + 1);
  }, []);

  // Handle authentication loading state
  if (isAuthLoading) {
    return (
        <div className="loading-overlay">
            <div className="loading-content">
                <Circles height="80" width="80" color="#ffffff" ariaLabel="loading" />
                <p>Loading... Syncing profile info</p>
            </div>
        </div>
    );
}

  // Conditional rendering based on step
  return (
    <div className="tool-container">
      {step === 1 && (
        <div className="upload-step">
          <h2 className="step-title">Step 1: Upload Image</h2>
          <input type="file" accept="image/*" onChange={handleImageChange} />
          {imageFile && <p>Selected image: {imageFile.name}</p>}
        </div>
      )}

      {step === 2 && (
        <div className="selection-step">
          <h2 className="step-title">Step 2: Select Gender and Region</h2>
          <h4>Selected image: {imageFile.name}</h4>

          <div className="selection-group">
            <h4>Select Gender</h4>
            <label>
              <input
                type="radio"
                value="M"
                checked={sesso === 'M'}
                onChange={(e) => setSesso(e.target.value)}
              />
              Male
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="F"
                checked={sesso === 'F'}
                onChange={(e) => setSesso(e.target.value)}
              />
              Female
            </label>
          </div>

          <div className="selection-group">
            <h4>Select Region</h4>
            <label>
              <input
                type="radio"
                value="Africa"
                checked={regione === 'Africa'}
                onChange={(e) => setRegione(e.target.value)}
              />
              Africa
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="America"
                checked={regione === 'America'}
                onChange={(e) => setRegione(e.target.value)}
              />
              America
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="Asia"
                checked={regione === 'Asia'}
                onChange={(e) => setRegione(e.target.value)}
              />
              Asia
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="Europe"
                checked={regione === 'Europe'}
                onChange={(e) => setRegione(e.target.value)}
              />
              Europe
            </label>
          </div>

          <div className="selection-group">
            <h4>Real Age (Optional)</h4>
            <input
              type="number"
              min="0"
              max="120"
              value={realAge}
              onChange={(e) => setRealAge(e.target.value)}
              placeholder="Enter real age (optional)"
              className="age-input"
            />
          </div>

          <button onClick={handleSaveSessoAndRegione} className="save-button">
            Save Data
          </button>
        </div>
      )}

      {step >= 3 && (imageFile || loadedSession) && userEmail && (
        <ImageSelector
          imageFile={imageFile}
          userEmail={userEmail}
          sesso={sesso}
          regione={regione}
          realAge={realAge}
          tipo={tipo}
          posizione={posizione}
          selectionRequired={true}
          maxTotalPoints={2}
          colorCategories={['blue']}
          categoryColors={{ blue: 'blue' }}
          passCategoryToAnnotation={true}
          maxPointsPerCategory={{ blue: 2 }}
          useLeftRightInNames={true}
          categoriesToAnnotate={['blue']}
          loadedSession={loadedSession}
          loadedSubImages={loadedSubImages} // Pass loadedSubImages here
          updateSessionsCount={updateSessionsCount}
        />
      )}



      {step === 1 && (
        <SavedSessionsGallery
          onSelectSession={handleSelectSession}
          sessionsCount={sessionsCount}
          userEmail={userEmail}
        />
      )}
    </div>
  );
}

export default ImageUpload;
